import { Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import PromptsSvg from '../../assets/icons/PromptsSvg';
import theme from '../../assets/theme';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { IconWrapper } from '../_atoms/IconWrapper';
import { Slide } from '../SlideComponents/slide.types';
import SlideWrapperViwer from '../SlideComponents/SlideWrapperViewer';
import { AnimatedHoverContainer } from './AnimatedHoverContainer';

export const PresentationCard = ({
  subTitle,
  title,
  slides,
  isHovering,
  isGenerator,
}: {
  title?: string;
  subTitle: string;
  slides: Slide[];
  isHovering: boolean;
  isGenerator?: boolean;
}) => {
  const visibleTitle = useMemo(() => {
    return title
      ? title.length > 30
        ? `${title.slice(0, 30)}...`
        : title
      : 'Untitled lesson';
  }, [title]);

  return (
    <AnimatedHoverContainer style={{ height: '100%', width: '100%', cursor: 'pointer' }}>
      <GeneratorCardContainer>
        <SlideTickingContainer slides={slides} isHovering={isHovering} />
        <HorisontalCardHeading>
          {isGenerator && (
            <Titlespan>
              <IconWrapper
                color={theme.colors.black}
                height={'20px'}
                width={'20px'}
                style={{ marginRight: '5px' }}
              >
                {<PromptsSvg />}
              </IconWrapper>
            </Titlespan>
          )}
          <Tooltip mouseEnterDelay={1.5} title={title}>
            <Titlespan>{visibleTitle}</Titlespan>
          </Tooltip>
        </HorisontalCardHeading>
        <HorisontalSubtext>{subTitle}</HorisontalSubtext>
      </GeneratorCardContainer>
    </AnimatedHoverContainer>
  );
};

const Titlespan = styled.span`
  height: 100%;
`;

const HorisontalSubtext = styled.p`
  height: 30px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0px;
  font-size: 11px;
  color: ${theme.colors.iconGrey};
  margin-top: 0px;
  @media (max-width: 800px) {
    visibility: hidden;
  }
`;

const HorisontalCardHeading = styled.h3`
  display: flex;
  align-items: center;
  height: 20px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  margin-top: 3px;
  margin-bottom: 3px;
  color: ${theme.colors.black};

  @media (max-width: 800px) {
    font-size: 14px;
  }
`;

const SlideTickingContainer = ({
  slides,
  isHovering,
}: {
  slides: Slide[];
  isHovering: boolean;
}) => {
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const isLargerThan800 = useBreakPoint(800);
  const slidesToPreview = useMemo(() => {
    return (slides || []).filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    );
  }, [slides]);

  useEffect(() => {
    if (!slidesToPreview) return;
    if (isHovering) {
      let timeout: NodeJS.Timeout;

      if (selectedSlideIndex === slidesToPreview.length - 1) {
        // When at the last slide, first reset to the first slide

        // Then, after a delay, move to the second slide
        timeout = setTimeout(() => {
          setSelectedSlideIndex(0);
        }, 1500);
      } else {
        // Normal behavior for other slides
        timeout = setTimeout(() => {
          setSelectedSlideIndex(selectedSlideIndex + 1);
        }, 1500);
      }

      return () => clearTimeout(timeout);
    } else {
      setSelectedSlideIndex(0);
    }
  }, [isHovering, slidesToPreview.length, selectedSlideIndex, slidesToPreview]);

  const slide = slidesToPreview[selectedSlideIndex];

  return (
    <div style={{ position: 'relative' }}>
      <SlidePreviewContainer>
        <div style={{ height: '100%', width: '100%', display: 'inline-block' }}>
          {slide && (
            <SlideWrapperViwer
              aspect={{ height: 1080, width: 1920 }}
              slide={slide}
              backgroundMedia={slide?.backgroundMedia}
              resultView={false}
              showStaticPoll={true}
              hideLogo={false}
              isDiscoverTemplate={false}
              isOwner={true}
            />
          )}
          {(isHovering || isLargerThan800) && slide && (
            <Label>
              {selectedSlideIndex + 1} of {slidesToPreview.length}
            </Label>
          )}
        </div>
      </SlidePreviewContainer>
    </div>
  );
};

const SlidePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 16px 18px; // 23 px horisontal padding to match the slide aspect ratio
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  & > div:first-child {
    border-radius: 10px;
    overflow: hidden;
    border: 2px solid ${theme.colors.greyLight};
  }

  @media (max-width: 800px) {
    & > div:first-child {
      border: 1px solid ${theme.colors.greyLight};
    }
    padding: 10px 12px;
  }
`;

const GeneratorCardContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: 1fr auto auto;
`;

const Label = styled.div`
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: #00000099;
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 12px;
  padding: 2px 10px;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
`;
